import { createNotifier, type Notifier } from './createNotifier';
import id from './id';
import memoizeWithWeakMap from './memoizeWithWeakMap';
import type { Store } from './useStoreState';
import WeakValueMap from './WeakValueMap';

function isFunction<State>(
  state: State | ((prevState: State) => State),
): state is (prevState: State) => State {
  return typeof state === 'function';
}

export interface StorageStore<in out State> extends Store<State> {
  getKey(): string;
  setState(state: State | ((prevState: State) => State)): void;
}

export interface Codec<in out State> {
  encode(state: State): string | null;
  decode(string: string | null): State;
}

const DEFAULT_CODEC: Codec<string | null> = {
  encode: id,
  decode: id,
};

type StorageStoreFactory = <State>(
  key: string,
  codec: Codec<State>,
) => StorageStore<State>;

function createStorageStoreFactory(storage: Storage): StorageStoreFactory {
  const notifiers = new WeakValueMap<string, Notifier>();

  function handleStorageEvent(storageEvent: StorageEvent) {
    if (storageEvent.storageArea === storage) {
      if (storageEvent.key === null) {
        for (const notifier of notifiers.values()) {
          notifier.notify();
        }
      } else {
        notifiers.get(storageEvent.key)?.notify();
      }
    }
  }

  return function createStorageStore(key, codec) {
    return {
      getKey() {
        return key;
      },

      unsafe_getState() {
        return codec.decode(storage.getItem(key));
      },

      setState(state) {
        const prevValue = storage.getItem(key);
        const prevState = codec.decode(prevValue);

        if (isFunction(state)) {
          state = state(prevState);
        }

        if (!Object.is(prevState, state)) {
          const value = codec.encode(state);

          if (!Object.is(prevValue, value)) {
            if (value === null) {
              storage.removeItem(key);
            } else {
              storage.setItem(key, value);
            }

            notifiers.get(key)?.notify();
          }
        }
      },

      subscribe(listener) {
        window.addEventListener('storage', handleStorageEvent);

        let notifier = notifiers.get(key);
        if (notifier == null) {
          notifier = createNotifier();
          notifiers.set(key, notifier);
        }

        const unsubscribe = notifier.listen(listener);

        return () => {
          unsubscribe();

          if (notifier.isEmpty()) {
            notifiers.delete(key);
          }

          if (notifiers.isEmpty()) {
            window.removeEventListener('storage', handleStorageEvent);
          }
        };
      },
    };
  };
}

const getStorageStoreFactory = memoizeWithWeakMap(createStorageStoreFactory);

export default function createStorageStore(
  storage: Storage,
  key: string,
): StorageStore<string | null>;

export default function createStorageStore<State>(
  storage: Storage,
  key: string,
  codec: Codec<State>,
): StorageStore<State>;

export default function createStorageStore(
  storage: Storage,
  key: string,
  codec: Codec<string | null> = DEFAULT_CODEC,
): StorageStore<string | null> {
  return getStorageStoreFactory(storage)(key, codec);
}
